import { useFeatureAccess } from '@/hooks/use-feature-access.hook';

/**
 * Whether the organization gets what Clu learns (CLE-562): the Memory page and
 * the rows under Clu's replies.
 *
 * True only once the entitlements say the organization is not AppSumo-only
 * (an AppSumo redemption with no paid plan), as the server's fence on
 * `/api/v1/memory` decides. Still loading, a failed first read of the
 * entitlements and no active organization all read as off, so the surface
 * never flashes. A failed background refetch keeps the last verdict, as
 * `useAgentsAccess` does, so a blip does not strip the rows from an open chat.
 * Every surface reads this one hook: the nav item, the home row, the page, the
 * chat rows and steps, the Skills tab's `Clu` tag and the agent page's chip.
 */
export function useCluMemoryEnabled(): boolean {
  return useCluMemoryAccess().enabled;
}

/**
 * `useCluMemoryEnabled` with whether it has answered: `ready` is false while
 * the entitlements are still loading, when `enabled` reads as off only because
 * nothing has answered yet (a nav that lays itself out once waits on it rather
 * than dropping the item in later).
 */
export function useCluMemoryAccess(): { enabled: boolean; ready: boolean } {
  const { isReady, organizationId, isAppSumoOnly } = useFeatureAccess();
  return {
    enabled: isReady && Boolean(organizationId) && !isAppSumoOnly,
    ready: isReady,
  };
}
